<template>
  <div class="logic-group">
    <div class="label-part">
      <div class="py-2.5 px-5 border border-element-neutral rounded-4 w-fit">
        AND
      </div>
    </div>
    <div class="statement-part">
      <template
        v-for="(statement, index) in logicGroup?.logicStatements ?? []"
        :key="statement['id']"
      >
        <div class="statement">
          <logic-statement
            v-model="logicGroup.logicStatements[index]"
            :statement-list="statementList"
            :errors="errors?.[statement.id]"
            :disabled="disabled"
            @remove="removeStatement(index)"
          />
        </div>
        <div v-if="!(index === numberOfStatements - 1 && disabled)" class="text-pre statement">
          <CustomButton
            v-if="index === numberOfStatements - 1"
            variant="text"
            class="text-pre !text-info-blue"
            @click="addStatement(index)"
          >
            + OR
          </CustomButton>
          <div v-else>
            OR
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import CustomButton from '@utils/CustomButton.vue';
import { v4 as uuidv4 } from 'uuid';
import { computed } from 'vue';
import type { LogicGroup, MaybeHydra, FilterPrototypeList } from '@/entities/Entities';
import LogicStatement from './LogicStatement.vue';

interface Props {
  modelValue?: MaybeHydra<LogicGroup>;
  statementList?: FilterPrototypeList;
  errors?: object;
  disabled?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'remove']);
const logicGroup = useVModel(props, 'modelValue', emit, { passive: true });

const numberOfStatements = computed(() => logicGroup?.value?.logicStatements?.length ?? 0);

const addStatement = (index: number) => {
  logicGroup?.value?.logicStatements?.splice(index + 1, 0, {
    id: uuidv4(),
    name: undefined,
    data: {},
  });
};

const removeStatement = (index: number) => {
  logicGroup.value?.logicStatements.splice(index, 1);
  if (numberOfStatements.value === 0) {
    emit('remove', props?.modelValue?.id);
  }
};
</script>

<style lang="scss" scoped>
.logic-group {
  @apply rounded-8 border border-element-neutral;
  @apply flex;
}

.label-part {
  @apply border-r border-r-element-neutral w-fit;
  @apply py-7 px-5;
  @apply flex items-center;
}

.statement-part {
  @apply flex flex-col items-start justify-center;
  @apply w-full;
}

.statement {
  @apply px-5 py-4;
  @apply w-full;
  @apply border-b border-element-neutral;
}

.statement:last-of-type {
  @apply border-none;
}
</style>
